import {Card, DropdownActionMenuItem, openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {head} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {DeleteMessageTemplateApiArg, useDeleteTemplateMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError, usePermissions} from '@dms/shared';

import {buildArray, composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export const MessageTemplates = () => {
  const navigate = useNavigate();
  const [deleteTemplateMutation] = useDeleteTemplateMutation();

  const [canCreateMessageTemplate] = usePermissions({
    permissionKeys: ['createMessageTemplate'],
  });

  const PREDEFINED_COLUMNS = ['type'] as const;

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
    getColumnData,
  }) => {
    const id = isArray(rowId) ? head(rowId) : rowId;

    const templateType = getColumnData<{key: string}>('type')?.value?.key?.toLowerCase() as
      | DeleteMessageTemplateApiArg['templateType']
      | undefined;

    if (!id || !templateType) {
      throw new Error('Invalid data for callback');
    }

    match(actionKey)
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteTemplateMutation({
              templateType,
              templateId: id,
            })
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('edit', 'detail', () =>
        navigate(
          composePath(settingsRoutes.messageTemplatesEdit, {params: {id, type: templateType}})
        )
      );
  };

  return (
    <SettingsTemplate
      data-testid={testIds.settings.customFields('template')}
      header={{
        title: i18n.t('page.messageTemplates.labels.list'),
        subtitle: i18n.t('page.messageTemplates.labels.listDescription'),
      }}
    >
      <Card
        actions={
          canCreateMessageTemplate
            ? [
                {
                  type: 'dropdown-button',
                  variant: 'link',
                  'data-testid': testIds.settings.messageTemplates('AddNew'),
                  menuItems: buildArray<DropdownActionMenuItem>()
                    .add({
                      'data-testid': testIds.settings.messageTemplates('AddNewSms'),
                      onClick: () =>
                        navigate(
                          composePath(settingsRoutes.messageTemplatesNew, {
                            params: {
                              type: 'sms',
                            },
                          })
                        ),
                      label: i18n.t('page.messageTemplates.labels.sms'),
                    })
                    .add({
                      'data-testid': testIds.settings.messageTemplates('Email'),
                      onClick: () =>
                        navigate(
                          composePath(settingsRoutes.messageTemplatesNew, {
                            params: {
                              type: 'email',
                            },
                          })
                        ),
                      label: i18n.t('page.messageTemplates.labels.email'),
                    })
                    .add({
                      'data-testid': testIds.settings.messageTemplates('AddNewChat'),
                      onClick: () =>
                        navigate(
                          composePath(settingsRoutes.messageTemplatesNew, {
                            params: {
                              type: 'chat',
                            },
                          })
                        ),
                      label: i18n.t('page.messageTemplates.labels.chat'),
                    }),
                  leftIcon: 'content/add',
                  title: i18n.t('page.messageTemplates.actions.addNewTemplate'),
                },
              ]
            : []
        }
        title={i18n.t('page.messageTemplates.labels.templateList')}
      >
        <DmsDataGrid
          autoHeight
          gridCode="messaging-context-template"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={testIds.settings.messageTemplates('dg')}
        />
      </Card>
    </SettingsTemplate>
  );
};
